import Phaser from '../lib/phaser.js';
import { DIRECTION } from '../common/direction.js';
import { TILE_SIZE } from '../config.js';
import { getTargetPositionFromGameObjectPositionAndDirection } from '../utils/grid-utils.js';

// ── Visible roaming overworld creature. ──
// A wild Catchimon that wanders within an encounter zone on the tile grid. Walking into it or
// clicking it starts a battle against its encounter area. Purely cosmetic until then: a gentle
// idle bob/hop tween sells "alive" without any physics. Cheap — no per-frame work beyond a
// timed wander step.

const MOB_SCALE = 1.0; // visible roaming beasts read clearly at ~one tile (80px sprite)
const MOB_DEPTH = 1; // below player/foreground (foreground is added later), above background

/**
 * @typedef RoamingMobConfig
 * @type {object}
 * @property {Phaser.Scene} scene
 * @property {import('../types/typedef.js').Coordinate} position grid-aligned spawn position
 * @property {string} texture creature texture key (the species' own assetKey)
 * @property {string} encounterAreaId the encounters.json area id this mob battles for
 * @property {number} monsterId the specific monster (monsters.json id) this mob IS — battling it
 *   spawns exactly this species so the wild creature on the map matches the one in battle
 * @property {number} [textureScale=1] sprite scale so the overworld mob reads at ~one tile
 * @property {(position: import('../types/typedef.js').Coordinate) => boolean} isBlockedFn collision test
 * @property {(mob: RoamingMob) => void} onClick invoked when the mob is clicked/tapped
 */

export class RoamingMob {
  /** @type {Phaser.Scene} */
  #scene;
  /** @type {Phaser.GameObjects.Image} */
  #sprite;
  /** @type {string} */
  #encounterAreaId;
  /** @type {number} the specific monsters.json id this mob represents (battle uses exactly this) */
  #monsterId;
  /** @type {(position: import('../types/typedef.js').Coordinate) => boolean} */
  #isBlocked;
  /** @type {{ x: number; y: number }} home tile — mob wanders within a small radius of it */
  #home;
  /** @type {number} next allowed wander time */
  #nextMoveTime;
  /** @type {boolean} */
  #isMoving;

  /**
   * @param {RoamingMobConfig} config
   */
  constructor(config) {
    this.#scene = config.scene;
    this.#encounterAreaId = config.encounterAreaId;
    this.#monsterId = config.monsterId;
    this.#isBlocked = config.isBlockedFn;
    this.#home = { x: config.position.x, y: config.position.y };
    this.#nextMoveTime = 0;
    this.#isMoving = false;

    // the mob reads at ~one tile; per-species `textureScale` normalizes the visible size since
    // species use different source sprite sizes (small 80px Catchimon vs 256px legacy sprites).
    const baseScale = config.textureScale ?? MOB_SCALE;

    this.#sprite = config.scene.add
      .image(config.position.x + TILE_SIZE / 2, config.position.y + TILE_SIZE / 2, config.texture)
      .setOrigin(0.5)
      .setScale(baseScale)
      .setDepth(MOB_DEPTH)
      .setInteractive({ useHandCursor: true });

    // gentle idle "breathing" squash/stretch so the creature reads as alive WITHOUT touching
    // its x/y (position is owned by the wander tweens, so we animate scale instead of y to
    // avoid fighting movement / tile math).
    config.scene.tweens.add({
      targets: this.#sprite,
      scaleX: baseScale * 1.06,
      scaleY: baseScale * 0.94,
      duration: 750,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.#sprite.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN,
      (/** @type {Phaser.Input.Pointer} */ pointer, _x, _y, event) => {
        event?.stopPropagation?.();
        config.onClick(this);
      }
    );
  }

  /** @type {Phaser.GameObjects.Image} */
  get sprite() {
    return this.#sprite;
  }

  /** @type {string} */
  get encounterAreaId() {
    return this.#encounterAreaId;
  }

  /** @type {number} the specific monsters.json id this roaming creature represents */
  get monsterId() {
    return this.#monsterId;
  }

  /**
   * Random grid wander within a 2-tile radius of home. Steps are collision-aware and tweened so
   * movement looks smooth. The sprite is centered on its tile (origin 0.5) so we track an
   * internal tile position via `#home`-relative math using the sprite center.
   * @param {DOMHighResTimeStamp} time
   * @returns {void}
   */
  update(time) {
    if (this.#isMoving || time < this.#nextMoveTime) {
      return;
    }

    // current grid tile (top-left) derived from the centered sprite
    const curTile = {
      x: Math.round((this.#sprite.x - TILE_SIZE / 2) / TILE_SIZE) * TILE_SIZE,
      y: Math.round((this.#sprite.y - TILE_SIZE / 2) / TILE_SIZE) * TILE_SIZE,
    };

    const dirs = [DIRECTION.UP, DIRECTION.DOWN, DIRECTION.LEFT, DIRECTION.RIGHT];
    Phaser.Utils.Array.Shuffle(dirs);

    for (const dir of dirs) {
      const next = getTargetPositionFromGameObjectPositionAndDirection(curTile, dir);
      // stay within a 2-tile radius of home and off blocked tiles
      const withinRadius =
        Math.abs(next.x - this.#home.x) <= TILE_SIZE * 2 && Math.abs(next.y - this.#home.y) <= TILE_SIZE * 2;
      if (!withinRadius || this.#isBlocked(next)) {
        continue;
      }
      this.#stepTo(next, time);
      return;
    }
    // nowhere to go this round — try again soon
    this.#nextMoveTime = time + Phaser.Math.Between(1500, 3000);
  }

  /**
   * Tween the sprite to a target tile center; gives a little hop for life.
   * @param {import('../types/typedef.js').Coordinate} tile grid-aligned target (top-left)
   * @param {DOMHighResTimeStamp} time
   * @returns {void}
   */
  #stepTo(tile, time) {
    this.#isMoving = true;
    const targetX = tile.x + TILE_SIZE / 2;
    const targetY = tile.y + TILE_SIZE / 2;
    this.#scene.tweens.add({
      targets: this.#sprite,
      x: targetX,
      y: targetY,
      duration: 600,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.#isMoving = false;
        this.#nextMoveTime = time + Phaser.Math.Between(2000, 4500);
      },
    });
  }

  /** @returns {void} */
  destroy() {
    this.#sprite?.destroy();
  }
}
